import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import HistoryIcon from '@mui/icons-material/History';
import NotificationTemplateLogs from './NotificationTemplateLogs';
import CloseIcon from '@mui/icons-material/Close';
import { notificationTemplateService } from '../../services/notificationTemplateService';

interface NotificationTemplate {
  _id: string;
  name: string;
  type: 'SYSTEM' | 'PROMOTIONAL' | 'TRANSACTIONAL' | 'EVENT';
  title: string;
  body: string;
  metadata?: Record<string, any>;
}

export default function NotificationTemplates() {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [formData, setFormData] = useState<Partial<NotificationTemplate>>({
    name: '',
    type: 'SYSTEM',
    title: '',
    body: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await notificationTemplateService.getTemplates();
      setTemplates(data);
    } catch (error) {
      showSnackbar('Failed to load templates', 'error');
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenDialog = (template?: NotificationTemplate) => {
    if (template) {
      setSelectedTemplate(template);
      setFormData(template);
    } else {
      setSelectedTemplate(null);
      setFormData({
        name: '',
        type: 'SYSTEM',
        title: '',
        body: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSaveTemplate = async () => {
    try {
      if (selectedTemplate) {
        // Edit existing template
        const updatedTemplate = await notificationTemplateService.updateTemplate(
          selectedTemplate._id,
          formData
        );
        setTemplates(templates.map(t => 
          t._id === selectedTemplate._id ? updatedTemplate : t
        ));
        showSnackbar(`Template "${formData.name}" updated successfully`, 'success');
      } else {
        // Create new template
        const newTemplate = await notificationTemplateService.createTemplate(formData as Omit<NotificationTemplate, '_id'>);
        setTemplates([...templates, newTemplate]);
        showSnackbar(`Template "${formData.name}" created successfully`, 'success');
      }
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Failed to save template. Please try again.', 'error');
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      const templateToDelete = templates.find(t => t._id === id);
      await notificationTemplateService.deleteTemplate(id);
      setTemplates(templates.filter(t => t._id !== id));
      showSnackbar(`Template "${templateToDelete?.name}" deleted successfully`, 'success');
    } catch (error) {
      showSnackbar('Failed to delete template. Please try again.', 'error');
      console.error('Error deleting template:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Available Templates
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setLogsDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            View Change History
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Template
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} md={6} key={template._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Box>
                    <IconButton onClick={() => handleOpenDialog(template)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteTemplate(template._id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  Type: {template.type}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  {template.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.body}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Template Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                required
                onChange={(e) => setFormData({ ...formData, type: e.target.value as NotificationTemplate['type'] })}
              >
                <MenuItem value="SYSTEM">System</MenuItem>
                <MenuItem value="PROMOTIONAL">Promotional</MenuItem>
                <MenuItem value="TRANSACTIONAL">Transactional</MenuItem>
                <MenuItem value="EVENT">Event</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notification Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              helperText="You can use variables like {{placeName}}"
            />
            <TextField
              label="Notification Body"
              fullWidth
              multiline
              required
              rows={4}
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              helperText="Use variables to make the message dynamic"
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Available Variables
                <Tooltip title="These variables will be replaced with actual values when the notification is sent">
                  <InfoIcon sx={{ ml: 1, fontSize: 16 }} />
                </Tooltip>
              </Typography>
              <Grid container spacing={1}>
                {[
                  { name: '{{placeName}}', desc: 'Name of the place' },
                  { name: '{{userName}}', desc: 'Name of the user' },
                  { name: '{{senderName}}', desc: 'Name of the sender' },
                  { name: '{{count}}', desc: 'Numerical value for achievements/milestones' },
                  { name: '{{eventName}}', desc: 'Name of the event' },
                  { name: '{{eventTime}}', desc: 'Time of the event' },
                  { name: '{{message}}', desc: 'Custom message content' },
                  { name: '{{promotionDetails}}', desc: 'Details of a promotion' },
                  { name: '{{neighborhoodName}}', desc: 'Name of the neighborhood' },
                ].map((variable) => (
                  <Grid item xs={12} sm={6} md={4} key={variable.name}>
                    <Tooltip title={variable.desc}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          cursor: 'help'
                        }}
                      >
                        {variable.name}
                      </Box>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <FormControl fullWidth>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Configuration
                <Tooltip title="Additional settings for this notification template">
                  <InfoIcon sx={{ ml: 1, fontSize: 16 }} />
                </Tooltip>
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.metadata?.requiresApproval || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          requiresApproval: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Requires Approval"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.metadata?.friendsOnly || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          friendsOnly: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Friends Only"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.metadata?.placeOwnersOnly || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          placeOwnersOnly: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Place Owners Only"
                />
              </Stack>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveTemplate} variant="contained">
            {selectedTemplate ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Template Change History
          <IconButton
            aria-label="close"
            onClick={() => setLogsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <NotificationTemplateLogs />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 